/**
 * قنوات ميتا: واتساب وماسنجر وإنستجرام.
 *
 * رسائل العملاء تصل إلى النظام بدل أن تبقى في هاتف من يردّ. وهي أهمّ قناة
 * تواصلٍ في السوق المصري: العميل يكتب على واتساب لا يُرسل بريدًا.
 *
 * ## ميتا تنادينا لا ننادِيها
 *
 * الوارد يصل عبر خطّاف ويب: ميتا تُرسل إلى مسارٍ عامّ عندنا كلّما وقع شيء.
 * ولذلك القسم الوارد كاملٌ ويعمل بمجرّد أن تُوجَّه إليه ميتا — أما الصادر
 * فيحتاج رمز وصولٍ للصفحة، وهو ما لا يُنشأ إلا بمعرّف تطبيق وسرّه.
 *
 * ## والتوقيع شرطٌ لا تحسين
 *
 * المسار عامٌّ يكتب رسائل في قاعدة بيانات شركة. وبلا تحقّقٍ من التوقيع
 * يستطيع أي أحد أن يُرسل إليه ما يشاء، فيزرع في محادثات الشركة رسائل لم
 * يكتبها عميل — وهي أخطر من العبث: قرارٌ يُبنى على كلامٍ ملفَّق.
 */
import type { Labelled } from './common.js';

/* ==========================================================================
   القنوات
   ========================================================================== */

export const META_CHANNEL_KINDS = ['WHATSAPP', 'MESSENGER', 'INSTAGRAM'] as const;
export type MetaChannelKind = (typeof META_CHANNEL_KINDS)[number];

/**
 * مزوّد القناة.
 *
 * META: اتصالٌ مباشر بواجهة ميتا الرسمية (graph.facebook.com).
 * ELMUJIB: عبر مزوّد واتساب «المجيب» — endpoint ومفتاحٌ خاصّان به.
 *
 * المزوّد يخصّ الواتساب وحده اليوم؛ ماسنجر وإنستجرام يبقيان على ميتا مباشرةً.
 */
export const META_PROVIDERS = ['META', 'ELMUJIB'] as const;
export type MetaProvider = (typeof META_PROVIDERS)[number];

export interface MetaProviderDef {
  key: MetaProvider;
  label: string;
  hint: string;
}

export const META_PROVIDER_DEFS: readonly MetaProviderDef[] = [
  { key: 'META', label: 'ميتا مباشرةً', hint: 'الاتصال بواجهة واتساب الرسمية برمزٍ من ميتا.' },
  {
    key: 'ELMUJIB',
    label: 'المجيب (elmujib)',
    hint: 'عبر مزوّد المجيب: رابطه ومعرّف المورّد ومفتاحه.',
  },
];

/** رابط المجيب الافتراضي — يُدخله المستخدم إن اختلف. */
export const ELMUJIB_DEFAULT_BASE_URL = 'https://elmujib.com/api';

export interface MetaChannelKindDef extends Labelled<MetaChannelKind> {
  /** ما يُطلب من ميتا لربطها. */
  needs: string;
}

export const META_CHANNEL_KIND_DEFS: readonly MetaChannelKindDef[] = [
  {
    key: 'WHATSAPP',
    label: 'واتساب للأعمال',
    kind: 'success',
    needs: 'معرّف رقم الواتساب (Phone Number ID) ورمز وصول دائم.',
  },
  {
    key: 'MESSENGER',
    label: 'ماسنجر',
    kind: 'info',
    needs: 'معرّف صفحة فيسبوك ورمز وصول الصفحة.',
  },
  {
    key: 'INSTAGRAM',
    label: 'إنستجرام',
    kind: 'brand',
    needs: 'معرّف حساب إنستجرام للأعمال ورمز وصول الصفحة المرتبطة.',
  },
] as const;

export const META_CHANNEL_KIND_MAP = Object.fromEntries(
  META_CHANNEL_KIND_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<MetaChannelKind, MetaChannelKindDef>>;

/* ==========================================================================
   الرسائل
   ========================================================================== */

export const META_DIRECTIONS = ['INBOUND', 'OUTBOUND'] as const;
export type MetaDirection = (typeof META_DIRECTIONS)[number];

export const META_MESSAGE_STATUSES = ['PENDING', 'SENT', 'DELIVERED', 'READ', 'FAILED'] as const;
export type MetaMessageStatus = (typeof META_MESSAGE_STATUSES)[number];

export const META_MESSAGE_STATUS_DEFS: readonly Labelled<MetaMessageStatus>[] = [
  { key: 'PENDING', label: 'في الانتظار', kind: 'neutral' },
  { key: 'SENT', label: 'أُرسلت', kind: 'info' },
  { key: 'DELIVERED', label: 'وصلت', kind: 'info' },
  { key: 'READ', label: 'قُرئت', kind: 'success' },
  { key: 'FAILED', label: 'فشلت', kind: 'danger' },
] as const;

export const META_MESSAGE_STATUS_MAP = Object.fromEntries(
  META_MESSAGE_STATUS_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<MetaMessageStatus, Labelled<MetaMessageStatus>>>;

/* ==========================================================================
   الحدود
   ========================================================================== */

export const MAX_META_MESSAGE_CHARS = 4096;
export const META_PAGE_SIZE = 50;

/**
 * نافذة الردّ في واتساب: أربع وعشرون ساعة.
 *
 * قاعدة ميتا لا قاعدتنا: بعد مضيّها لا يُقبل إلا قالبٌ معتمَد مسبقًا، وأي
 * رسالة حرّة تُرفض. وإعلانُها في الشاشة يمنع من يكتب من أن يظنّ أن رسالته
 * وصلت وهي مرفوضة عند ميتا.
 */
export const WHATSAPP_REPLY_WINDOW_HOURS = 24;

export function withinReplyWindow(lastInboundAt: string | Date | null, now = new Date()): boolean {
  if (!lastInboundAt) return false;
  const at = typeof lastInboundAt === 'string' ? new Date(lastInboundAt) : lastInboundAt;
  return now.getTime() - at.getTime() <= WHATSAPP_REPLY_WINDOW_HOURS * 3600_000;
}

/* ==========================================================================
   الشكل المعاد
   ========================================================================== */

export interface MetaChannelView {
  id: string;
  kind: MetaChannelKind;
  name: string;
  /** معرّف الصفحة أو الرقم في ميتا — يُعرض ليُطابَق بما في لوحة ميتا. */
  externalId: string;
  isActive: boolean;
  /** هل لها رمز وصول محفوظ — بدونه لا إرسال. */
  canSend: boolean;
  /** المزوّد — META أو ELMUJIB. */
  provider: MetaProvider;
  /** رابط واجهة المزوّد ومعرّف المورّد، للعرض والتعديل (ليسا سرًّا كالرمز). */
  providerBaseUrl: string | null;
  providerVendorId: string | null;
  lastEventAt: string | null;
  conversationCount: number;
  createdAt: string;
}

export interface MetaConversationView {
  id: string;
  channelId: string;
  channelKind: MetaChannelKind;
  channelName: string;
  /** رقم العميل أو معرّفه عند ميتا. */
  externalId: string;
  contactName: string | null;
  contactPhone: string | null;
  leadId: string | null;
  leadName: string | null;
  accountId: string | null;
  accountName: string | null;
  lastBody: string | null;
  lastAt: string | null;
  /** آخر رسالة واردة — عليها تُحسب نافذة الردّ. */
  lastInboundAt: string | null;
  /** من يتولّاها — معرّف مستخدم، أو فارغٌ إن كانت عامّة. */
  assigneeId: string | null;
  unread: number;
}

/** ملخّص العميل خلف المحادثة — لِلوحة التفاصيل. */
export interface MetaConversationDetail {
  id: string;
  channelKind: MetaChannelKind;
  channelName: string;
  contactName: string | null;
  contactPhone: string | null;
  externalId: string;
  assigneeId: string | null;
  leadId: string | null;
  accountId: string | null;
  /** ملخّص السجلّ المربوط، إن وُجد. */
  record:
    | null
    | {
        kind: 'lead' | 'account';
        name: string;
        status: string | null;
        rating: string | null;
        value: number | null;
        source: string | null;
        ownerId: string | null;
        email: string | null;
        extra: string | null;
        lastContactedAt: string | null;
      };
  /** آخر تغذيةٍ راجعة على السجلّ المربوط. */
  lastFeedback: null | { body: string | null; authorName: string | null; createdAt: string };
}

export interface MetaMessageView {
  id: string;
  conversationId: string;
  direction: MetaDirection;
  body: string | null;
  mediaType: string | null;
  status: MetaMessageStatus;
  /** سبب الفشل كما ورد من ميتا — «فشلت» بلا سبب لا تُصلَح. */
  error: string | null;
  sentAt: string;
  authorName: string | null;
}

/**
 * حالة الربط بميتا.
 *
 * تُعرض كما هي بلا تجميل: شاشةٌ تقول «متصل» وهي ليست كذلك تجعل من يعتمد
 * عليها يفقد رسائل عملاء ولا يدري.
 */
export interface MetaStatus {
  /** `META_APP_ID` مضبوط. */
  hasAppId: boolean;
  /** `META_APP_SECRET` مضبوط — وبدونه لا يُقبل خطّاف. */
  hasAppSecret: boolean;
  /** `META_VERIFY_TOKEN` مضبوط — وبدونه لا تُثبَّت المناداة. */
  hasVerifyToken: boolean;
  /** عنوان الخطّاف الذي يُوضع في لوحة ميتا. */
  webhookUrl: string;
  /** جاهزٌ لاستقبال الوارد. */
  canReceive: boolean;
  /** عنوان خطّاف المجيب — يُوضع في «إعدادات ← API Access & Webhook» عندهم. */
  elmujibWebhookUrl: string;
  /** سرّ خطّاف المجيب مضبوط — بدونه يُرفض خطّافه. */
  elmujibReady: boolean;
}

/** ما ينقص لتعمل القناة — جملةٌ تُقرأ لا قائمة أعلام. */
export function missingSetup(status: MetaStatus): string[] {
  const gaps: string[] = [];
  if (!status.hasAppSecret) gaps.push('سرّ التطبيق (META_APP_SECRET) — بدونه يُرفض كل خطّاف.');
  if (!status.hasVerifyToken)
    gaps.push('رمز التثبيت (META_VERIFY_TOKEN) — بدونه لا تقبل ميتا تسجيل العنوان.');
  if (!status.hasAppId) gaps.push('معرّف التطبيق (META_APP_ID) — يلزم لإنشاء رموز الوصول.');
  return gaps;
}
